import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Plus, Trash2, MessageCircle } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { PageTitle, AdminButton, useList } from "@/components/admin/kit";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useUnsaved, UnsavedBadge, same } from "@/lib/unsaved";
import { KnowledgeDocs } from "@/components/admin/KnowledgeDocs";
import { useTerms } from "@/lib/industry";

const IDEAS = {
  builder: ["What are the HOA / condo fees?", "Which schools is this community zoned for?", "What warranty comes with the home?", "When can I move in?", "Can I make structural changes?"],
  fitness: ["What should I bring to my first session?", "Is there parking?", "Do you offer a free trial class?", "Are there showers and lockers?", "How do I book a session?"],
  energy: ["What PPE is required on site?", "Who do I call in an emergency?", "What are the site access hours?", "Who is the operator of this asset?", "What is the muster point?"],
};

function TourUsage({ t }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/ai-usage").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  if (!rows) return null;
  const max = Math.max(1, ...rows.map((r) => r.questions));
  return (
    <div className="rounded-3xl bg-white border p-6" data-testid="ai-usage-by-tour">
      <p className="font-semibold">Questions by {t.item} this month</p>
      <p className="text-xs text-slate-500 mb-4">Which tours your AI assistant is busiest on. Resets on the 1st.</p>
      {rows.length === 0 && <p className="text-sm text-slate-500" data-testid="ai-usage-by-tour-empty">No questions yet this month.</p>}
      <div className="space-y-3">{rows.map((r, i) => (
        <div key={r.house_type_id + r.name} data-testid={`ai-usage-tour-${i}`}>
          <div className="flex justify-between text-sm"><span className="truncate pr-2">{r.name}</span><span className="font-semibold tabular-nums" data-testid={`ai-usage-tour-count-${i}`}>{r.questions.toLocaleString()}</span></div>
          <div className="h-1.5 rounded-full bg-slate-100 mt-1 overflow-hidden"><div className="h-full bg-bp transition-[width] duration-700" style={{ width: `${(r.questions / max) * 100}%` }} /></div>
        </div>
      ))}</div>
    </div>
  );
}

function Usage({ plan, topups, onRequest, price, t }) {
  if (!plan) return null;
  const lim = plan.ai_chats;
  const pending = (topups || []).find((t) => t.status === "pending");
  const pct = lim ? Math.min(100, Math.round((plan.ai_used / lim) * 100)) : 0;
  const bar = pct >= 100 ? "bg-rose-500" : pct >= 80 ? "bg-amber-500" : "bg-emerald-500";
  return (
    <div className="rounded-3xl bg-white border p-6" data-testid="ai-usage">
      <div className="flex items-center gap-3"><MessageCircle className="w-5 h-5 text-slate-500" /><p className="font-semibold flex-1">AI chats this month</p>
        <p className="font-serif text-2xl" data-testid="ai-usage-count">{plan.ai_used}{lim ? <span className="text-base text-slate-500"> / {lim.toLocaleString()}</span> : <span className="text-base text-slate-500"> · no limit</span>}</p></div>
      {lim && <div className="h-3 rounded-full bg-slate-100 mt-4 overflow-hidden"><div className={`h-full ${bar} transition-[width] duration-700`} style={{ width: `${pct}%` }} data-testid="ai-usage-bar" /></div>}
      <p className="text-xs text-slate-500 mt-3">One chat = one {t.person.toLowerCase()} question. Your {plan.name} plan includes {lim ? `${(lim - (plan.ai_extra || 0)).toLocaleString()} a month` : "unlimited chats"}{plan.ai_extra ? `, plus ${plan.ai_extra.toLocaleString()} extra this month` : ""}, reset on the 1st.{lim && pct >= 100 ? ` The limit is reached, so ${t.who} are asked to contact you instead.` : lim && pct >= 80 ? " You're close to the limit." : ""}</p>
      {lim && (pending
        ? <p className="mt-4 text-sm rounded-xl bg-amber-50 text-amber-800 px-3 py-2" data-testid="ai-topup-pending">Top-up of {pending.chats.toLocaleString()} chats requested. We'll approve it and invoice you shortly.</p>
        : <AdminButton variant="ghost" className="mt-4 w-full" onClick={onRequest} data-testid="ai-topup-request"><Plus className="w-4 h-4" />Request 1,000 more chats{price != null ? ` · $${price} CAD` : ""}</AdminButton>)}
    </div>
  );
}

export default function AdminAssistant() {
  const [b, setB] = useState(null);
  const [orig, setOrig] = useState(null);
  const [plan, setPlan] = useState(null);
  const [topups, setTopups] = useState([]);
  const [price, setPrice] = useState(null);
  const loadTopups = () => api.get("/admin/ai-topups").then((r) => setTopups(r.data)).catch(() => {});
  const requestTopup = async () => {
    if (!window.confirm(`Request 1,000 extra AI chats for this month${price != null ? ` for $${price} CAD + GST` : ""}? We'll approve it and send you an invoice.`)) return;
    try { await api.post("/admin/ai-topups"); toast.success("Request sent. We'll be in touch with an invoice."); loadTopups(); } catch (e) { toast.error(errMsg(e)); }
  };
  const [homes, reloadHomes] = useList("/admin/house-types");
  const { t, ind } = useTerms();
  const ideas = IDEAS[ind];
  const [target, setTarget] = useState("");
  const home = (homes || []).find((h) => h.id === target);
  const [hf, setHf] = useState([]);
  useEffect(() => { setHf(home?.faqs || []); }, [home]);
  useEffect(() => {
    api.get("/admin/builder").then((r) => { const d = { ...r.data, faqs: r.data.faqs || [] }; setB(d); setOrig(d); });
    api.get("/admin/plan").then((r) => setPlan(r.data)).catch(() => {});
    loadTopups();
    api.get("/public/pricing-extras").then((r) => setPrice(r.data.topup_price)).catch(() => {});
  }, []);
  const dirty = (!!b && (!same(b.faqs, orig?.faqs) || !same(b.chat_suggestions || [], orig?.chat_suggestions || []))) || (!!home && !same(hf, home.faqs || []));
  useUnsaved(dirty);
  if (!b) return null;
  const faqs = home ? hf : b.faqs;
  const setFaqs = (v) => (home ? setHf(v) : setB({ ...b, faqs: v }));
  const upd = (i, k, v) => setFaqs(faqs.map((f, j) => (j === i ? { ...f, [k]: v } : f)));
  const add = (q = "") => setFaqs([...faqs, { q, a: "" }]);
  const save = async () => {
    const clean = faqs.filter((f) => f.q.trim() && f.a.trim());
    try {
      if (home) { await api.put(`/admin/house-types/${home.id}`, { ...home, faqs: clean }); reloadHomes(); toast.success(`FAQs saved for ${home.name}`); return; }
      const { data } = await api.put("/admin/builder", { ...b, faqs: clean, chat_suggestions: (b.chat_suggestions || []).map((q) => q.trim()).filter(Boolean) }); const d = { ...data, faqs: data.faqs || [] }; setB(d); setOrig(d); toast.success("Saved. The assistant uses them straight away.");
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="admin-assistant-page">
      <PageTitle title="AI assistant" sub={`The assistant answers ${t.who} using each ${t.item}'s details, the FAQs and any documents you upload below. It never makes things up.`} right={<div className="flex items-center gap-3"><UnsavedBadge show={dirty} /><AdminButton onClick={save} data-testid="faq-save"><Check className="w-4 h-4" />Save FAQs</AdminButton></div>} />
      <div className="grid lg:grid-cols-[1fr_340px] gap-6 items-start">
        <div className="space-y-6">
        <div className="rounded-3xl bg-white border p-6 space-y-4" data-testid="faq-list">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[200px]"><p className="font-semibold">{home ? `FAQs for ${home.name} only` : `FAQs for all ${t.items}`}</p><p className="text-xs text-slate-500">{home ? `Use these for things that only apply to this ${t.item}. They win over account-wide answers.` : "Write answers the way you'd want your team to say them."}</p></div>
            <select value={target} onChange={(e) => setTarget(e.target.value)} className="h-10 rounded-xl border px-3 text-sm bg-white" data-testid="faq-target">
              <option value="">All {t.items}</option>
              {(homes || []).map((h) => <option key={h.id} value={h.id}>{h.name}{h.faqs?.length ? ` (${h.faqs.length})` : ""}</option>)}
            </select>
          </div>
          {faqs.length === 0 && <p className="text-sm text-slate-500">No FAQs yet. Start with one of the ideas on the right.</p>}
          {faqs.map((f, i) => (
            <div key={i} className="rounded-2xl border p-4 space-y-2" data-testid={`faq-row-${i}`}>
              <div className="flex gap-2"><Input value={f.q} maxLength={200} onChange={(e) => upd(i, "q", e.target.value)} placeholder={`Question, e.g. ${ideas[0]}`} data-testid={`faq-q-${i}`} />
                <button onClick={() => setFaqs(faqs.filter((_, j) => j !== i))} className="w-10 h-10 shrink-0 rounded-lg text-rose-600 hover:bg-rose-50 grid place-items-center" title="Delete" data-testid={`faq-delete-${i}`}><Trash2 className="w-4 h-4" /></button></div>
              <Textarea rows={3} maxLength={1500} value={f.a} onChange={(e) => upd(i, "a", e.target.value)} placeholder="Answer" data-testid={`faq-a-${i}`} />
            </div>
          ))}
          <AdminButton variant="ghost" onClick={() => add()} data-testid="faq-add"><Plus className="w-4 h-4" />Add a question</AdminButton>
        </div>
          <KnowledgeDocs home={home} t={t} />
        </div>
        <div className="space-y-6">
          <Usage plan={plan} topups={topups} onRequest={requestTopup} price={price} t={t} />
          <TourUsage t={t} />
          <div className="rounded-3xl bg-white border p-6 space-y-2" data-testid="chat-suggestions-editor">
            <p className="font-semibold">Suggested questions</p>
            <p className="text-xs text-slate-500">Shown as tap-to-ask buttons in the chat (up to 8, for all {t.items}). Click Save FAQs to save.</p>
            {(b.chat_suggestions || []).map((q, i) => (
              <div key={i} className="flex gap-2"><Input value={q} maxLength={120} onChange={(e) => setB({ ...b, chat_suggestions: b.chat_suggestions.map((x, j) => (j === i ? e.target.value : x)) })} data-testid={`chat-suggestion-input-${i}`} />
                <button onClick={() => setB({ ...b, chat_suggestions: b.chat_suggestions.filter((_, j) => j !== i) })} className="w-10 h-10 shrink-0 rounded-lg text-rose-600 hover:bg-rose-50 grid place-items-center" data-testid={`chat-suggestion-delete-${i}`}><Trash2 className="w-4 h-4" /></button></div>
            ))}
            {(b.chat_suggestions || []).length < 8 && <AdminButton variant="ghost" onClick={() => setB({ ...b, chat_suggestions: [...(b.chat_suggestions || []), ""] })} data-testid="chat-suggestion-add"><Plus className="w-4 h-4" />Add suggested question</AdminButton>}
          </div>
          <div className="rounded-3xl bg-white border p-6" data-testid="faq-ideas">
            <p className="font-semibold mb-3">Ideas {t.who} often ask</p>
            <div className="flex flex-wrap gap-2">{ideas.map((q, i) => <button key={q} onClick={() => add(q)} className="text-left text-xs rounded-full border px-3 py-1.5 hover:border-slate-500" data-testid={`faq-idea-${i}`}>+ {q}</button>)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
